/*
 * ******************************************************
 * Copyright (c) 2022 Jenke & Rauschert GbR <info@aiandor.de> - All Rights Reserved
 *
 * This file is part of a software product owned by the Jenke & Rauschert GbR.
 * Unauthorized distribution of this file is strictly prohibited.
 * ******************************************************
 */

import {
    Alert,
    Breadcrumb,
    Card,
    Descriptions,
    Modal,
    notification,
    Result,
    Space,
    Spin,
    Tabs,
    Tag,
    Tooltip
} from "antd";
import {Link, useParams} from "react-router-dom";
import {Customer, Device, Todo} from "../core/datamodel";
import {del, get, patch} from "../core/request";
import React, {useEffect, useState} from "react";
import {useAppDispatch, useAppSelector} from "../store";
import {ViewState} from "../states";
import {openDrawer, openUpload} from "../reducers/drawers-reducer";
import {CustomButton} from "../components/buttons";
import {IconSet} from "../components/icons";
import Loading from "../components/loading";
import {ApiTable} from "../components/table";
import {triggerReload} from "../reducers/reloadsignal-reducer";
import NoteCard from "../components/noteCard";

export default function DeviceDetailsView(props: any) {
    const params: { id: string } = useParams();
    const reloadSignal = useAppSelector(state => state.reloadSignals.device)
    const [device, setDevice] = useState({} as Device);
    const [state, setState] = useState(ViewState.loading);
    const [permissions, setPermissions] = useState({} as any);

    const [customer, setCustomer] = useState({} as Customer);
    const customerReloadSignal = useAppSelector(state => state.reloadSignals.customer)


    useEffect(() => {
        get("devices/" + params.id).done((response) => {
                setDevice(new Device(response.data.device));
                setState(ViewState.success);
            }
        ).fail(() => setState(ViewState.failed))
    }, [params.id, reloadSignal, useAppSelector(state => state.reloadSignals.mappings)])

    useEffect(() => {
        get("devices/" + params.id + "/permissions").done((response) => {
                setPermissions(response.data.permissions);
            }
        )
    }, [params.id, reloadSignal])

    useEffect(() => {
        if (!!device?.props?.customer?.props?.uuid) {
            get("customers/" + device.props.customer.props.uuid).done(
                (response) => {
                    setCustomer(new Customer(response.data.customer));
                }
            )
        }
    }, [device?.props?.customer, customerReloadSignal])

    let dispatch = useAppDispatch();

    const onEdit = () => dispatch(openDrawer({key: 'device', object: device}));

    const onSave = (data: string) => {
        patch("devices/" + params.id, {"comment": data}).done((response) => {
            setDevice(new Device(response.data.device))
        }).fail(() => notification.error({
            message: "Fehler",
            description: "Notiz konnte nicht gespeichert werden.",
            duration: 30,
        }))
    }

    const breadcrumb = (
        <Breadcrumb style={{paddingTop: 6, paddingBottom: 6}}>
            <Breadcrumb.Item><Link to="/">Home</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to="/customers">Kunden</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link
                to={"/customers/" + customer.props?.uuid}>{!!customer.props?.repr ? customer.props.repr : "Unknown"}</Link></Breadcrumb.Item>
            <Breadcrumb.Item>{!!device?.props?.name ? device.props.name : "Unknown"}</Breadcrumb.Item>
        </Breadcrumb>
    )

    const get_location = () => {
        if (!!device?.props &&
            (!!device.props.street || !!device.props.house_nr ||
                !!device.props.additional_adresse ||
                !!device.props.postcode ||
                !!device.props.city)
        ) {
            return <Tooltip overlay="Gerätestandort">
                {!!device?.props?.street || !!device?.props?.house_nr ?
                    (<>{device?.props?.street} {device?.props?.house_nr}<br/></>) : (<></>)}
                {device?.props?.additional_adresse ? (<>{device?.props?.additional_adresse}<br/></>) : (<></>)}
                {device?.props?.postcode} {device?.props?.city}
            </Tooltip>
        } else {
            if (!!customer?.props) {
                return <Tooltip overlay="Kunden-Adresse">
                    <span style={{color: '#6c757d'}}>
                    {customer?.props?.street} {customer?.props?.house_nr} <br/>
                        {customer?.props?.additional_adresse ? (<>{customer?.props?.additional_adresse}<br/></>) : (<></>)}
                        {customer?.props?.postcode} {customer?.props?.city}
                    </span>
                </Tooltip>
            } else {
                return ""
            }
        }
    }

    const pageHeader = (
        <Card
            title={device?.props?.name}
            extra={
                <Space>
                    <Link to={"/devices/" + params.id + "/report"}>
                        <CustomButton mode={'report'} tooltip={"Berichte erzeugen"}/>
                    </Link>
                    <CustomButton mode={'upload'}
                                  onClick={() => dispatch(openUpload(device.props.uuid ? device.props.uuid : ""))
                                  }
                                  tooltip={"Daten hochladen"}/>
                    <CustomButton
                        mode={'edit'}
                        onClick={onEdit}
                        tooltip={"Gerät bearbeiten"}
                        disabled={!(
                            !!permissions?.device?.delete
                            || !!permissions?.device?.put
                            || !!permissions?.device?.patch
                        )}
                    />
                </Space>
            }>
            <Descriptions colon={false} size="small">
                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Gerätmodell"}>
                            <IconSet type='models' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={<Tooltip overlay={"Gerätemodell"}>
                        {device?.props?.device_model?.props?.repr}
                    </Tooltip>}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Kontaktpersonen"}>
                            <IconSet type='persons' style={{lineHeight: 32, alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={
                        !!device?.props?.contact_persons ?
                            <Tooltip overlay={"Kontaktperson"}>
                                {
                                    device.props.contact_persons.map(
                                        (p: any) =>
                                            <Link to={"/persons/" + p.props.uuid}
                                                  children={<Tag>{p.props.repr}</Tag>}/>
                                    )
                                }
                            </Tooltip>
                            : <></>
                    }
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Kunde"}>
                            <IconSet type='customers' style={{lineHeight: 32, alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={
                        !!device?.props?.customer ?
                            <Tooltip overlay={"Kunde"}>
                                <Link to={"/customers/" + device.props.customer.props?.uuid}
                                      children={device.props.customer.props?.repr}/>
                            </Tooltip>
                            : <></>
                    }
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Software"}>
                            <IconSet type='software' style={{lineHeight: 32, alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={<Tooltip overlay={"Software"}>
                        {device?.props?.software}
                    </Tooltip>}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Synology-Link"}>
                            <IconSet type='link' style={{lineHeight: 32, alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={<Tooltip overlay={"Synology-Link"}>
                        {device?.props?.synologylink ?
                            <a href={device?.props?.synologylink}
                               target="_blank">{device?.props?.synologylink}</a> : ""
                        }
                    </Tooltip>}
                />

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Abrechnungsinformationen"}>
                            <IconSet type='coins' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={<Tooltip overlay={"Abrechnungsinformationen"}>
                        {device?.props?.notes_for_billing}
                    </Tooltip>}
                />

                <Descriptions.Item
                    label={<Tooltip overlay={"Baujahr"}>
                        <IconSet type='calendar' style={{lineHeight: '32px', alignSelf: 'center'}}/>
                    </Tooltip>}
                    children={<Tooltip overlay={"Baujahr"}>
                        {device?.props?.manufacture_date?.format("YYYY")}
                    </Tooltip>}
                />

                {
                    device?.props?.device_model?.props?.type === "CT" ? (
                        <>
                            <Descriptions.Item
                                label={"Zeilenzahl:"}
                                children={device?.props?.number_rows}
                            />

                            <Descriptions.Item
                                label={"Rekonstruktion:"}
                                children={device?.props?.iterativerecon}
                            />
                        </>
                    ) : (<></>)
                }

                {
                    device?.props?.device_model?.props?.type === "MG" ? (
                        <Descriptions.Item
                            label={"tomosynthese:"}
                            children={
                                device?.props?.tomosynthese ?
                                    <Tooltip overlay={"Ja"}>
                                        <IconSet type={'yes'} style={{lineHeight: 32, alignSelf: 'center'}}/>
                                    </Tooltip>
                                    :
                                    <Tooltip overlay={"Nein"}>
                                        <IconSet type={'no'} style={{lineHeight: 32, alignSelf: 'center'}}/>
                                    </Tooltip>
                            }
                        />
                    ) : (<></>)
                }
                {
                    device?.props?.device_model?.props?.type === "XR" ? (
                        <Descriptions.Item
                            label={"CR/DX:"}
                            children={device?.props?.crdx}
                        />
                    ) : (<></>)
                }

                <Descriptions.Item
                    label={
                        <Tooltip overlay={"Standort"}>
                            <IconSet type='address' style={{lineHeight: 32, alignSelf: 'center'}}/>
                        </Tooltip>}
                    children={get_location()}
                />


            </Descriptions>
        </Card>
    )

    const disabledAlert = (
        !!device?.props?.disabled ? (<Alert
            message="Dieses Gerät ist deaktiviert!"
            description="Zur weiteren Verwendnung bearbeiten und reaktivieren."
            type="warning"
            showIcon={true}
            style={{marginBottom: 24}}
        />) : (<></>)
    )

    const petAlert = (
        device?.props?.device_model?.props?.type === "PET/CT" ? (<Alert
            message="Bitte den Gerätetyp ändern!"
            description="Der Gerätetyp PET/CT wird in den nächsten Versionen entfernt. Geräte dieser Art sollen doppelt angelegt und als CT und NM erfasst werden."
            type="warning"
            showIcon={true}
            style={{marginBottom: 24}}
        />) : (<></>)
    )

    const showUploadDetails = (record: any) => {
        Modal.info({
            title: "Upload Details",
            maskClosable: true,
            content: (
                <div style={{width: "100%"}}>
                    <p>Details werden geladen...</p>
                    <p><Spin/></p>
                </div>
            ),
        })
        get("devices/upload/" + record.key).done((response) => {
            Modal.destroyAll()
            console.debug(response)
            Modal.info({
                title: "Upload Details",
                maskClosable: true,
                content: (
                    <div>
                        {response.data.order.map((p: any) => {
                            return (
                                <p>{p}: {response.data.data[p]}</p>
                            )
                        })}
                    </div>
                ),
                onOk() {
                },
            })
        }).fail(() => {
            Modal.destroyAll()
            Modal.error({
                title: "Fehler beim Laden",
                content: "Die Details des Uploads konnten nicht geladen werden!",
                maskClosable: true,
            })
        })
    }

    const tabItems = [
        {
            key: '1',
            forceRender: true,
            disabled: !device?.props?.comment,
            label: <><IconSet type='notes'/> Notizen</>,
            children: <NoteCard text={device?.props?.comment ? device?.props?.comment : ""} onSave={onSave}/>

        },
        {
            key: '2',
            forceRender: false,
            label: <><IconSet type='history'/> Verlauf</>,
            children: <ApiTable
                title={<>Verlauf der letzten 12 Monate</>}
                id="Device_History"
                url={"devices/" + device?.props?.uuid + "/history"}
                reloadSignals={[
                    useAppSelector(state => state.reloadSignals.manualEntry),
                    useAppSelector(state => state.reloadSignals.upload),
                ]}
            />
        },
        {
            key: '3',
            forceRender: false,
            label: <><IconSet type='upload-list'/> Uploads</>,
            children: <ApiTable
                title={<>Daten-Uploads</>}
                id="Device_manualEntrys"
                url={"devices/" + device?.props?.uuid + "/uploadlist"}
                reloadSignals={[useAppSelector(state => state.reloadSignals.manualEntry), useAppSelector(state => state.reloadSignals.upload)]}
                actionWidth={50}
                actions={(text: any, record: any) => (
                    <Tooltip overlay={"Daten löschen"}>
                        <IconSet
                            type='delete'
                            //@ts-ignore
                            className="action-delete"
                            //@ts-ignore
                            onClick={(e) => {
                                e.stopPropagation();
                                if (!record['upload'].includes("(")) {
                                    Modal.confirm({
                                        title: "Manuellen Eintrag '" + record['upload'] + "' wirklich löschen?",
                                        content: "Der Eintrag wird unwiederruflich gelöscht!",
                                        maskClosable: true,
                                        cancelText: 'Abbrechen',
                                        okText: 'Löschen',
                                        okType: 'danger',
                                        autoFocusButton: 'cancel',
                                        onOk: () => {
                                            del("manualentrys/" + record.key).then(() => {
                                                    dispatch(triggerReload('manualEntry'))
                                                }
                                            )
                                        }
                                    });
                                } else {
                                    Modal.confirm({
                                        title: "Upload wirklich löschen?",
                                        content: "Alle erzeugen Einträge werden unwiederruflich gelöscht!",
                                        maskClosable: true,
                                        cancelText: 'Abbrechen',
                                        okText: 'Löschen',
                                        okType: 'danger',
                                        autoFocusButton: 'cancel',
                                        onOk: () => {
                                            del("uploads/" + record.key).then(() => {
                                                    dispatch(triggerReload('upload'))
                                                }
                                            )
                                        }
                                    });
                                }


                                return true;
                            }}
                        />
                    </Tooltip>
                )}
                onRow={showUploadDetails}
            />
        },
        /* {
            key: '4',
            forceRender: true,
            label: <><IconSet type='exam'/> Untersuchungen</>,
            children: <DeviceDataTable
                title={<>Untersuchungen</>}
                id="Device_Exams"
                url={"devices/" + device?.props?.uuid + "/exams"}
                type={device?.props?.device_model?.props?.type ? device?.props?.device_model?.props?.type : "CT"}
                clickable='exam'
                reloadSignals={[useAppSelector(state => state.reloadSignals.exam), useAppSelector(state => state.reloadSignals.event)]}
                // @ts-ignore
                pagination={{
                    showTotal: (total: string) => total + ' Einträge',
                    size: 'default',
                    position: ['none', 'bottomCenter'],
                    pageSizeOptions: [10, 20, 50, 100, 200, 500]
                }}
                buttons={
                    <CustomButton
                        mode={"add"}
                        tooltip={"Neue Untersuchung hinzufügen"}
                        onClick={() => dispatch(openDrawer({
                            //@ts-ignore
                            key: 'exam', object: [
                                device?.props?.device_model?.props?.type ? device?.props?.device_model?.props?.type : "CT",
                                undefined,
                                device?.props?.uuid
                            ]
                        }))}/>
                }
            />
        }, */
        /*{
            key: '5',
            forceRender: true,
            label: <><IconSet type='event'/> Serien </>,
            children: <DeviceDataTable
                title={<>Untersuchungen</>}
                id="Device_Events"
                url={"devices/" + device?.props?.uuid + "/events"}
                type={device?.props?.device_model?.props?.type ? device?.props?.device_model?.props?.type : "CT"}
                clickable='event'
                reloadSignals={[useAppSelector(state => state.reloadSignals.event)]}
                // @ts-ignore
                pagination={{
                    showTotal: (total: string) => total + ' Einträge',
                    size: 'default',
                    position: ['none', 'bottomCenter'],
                    pageSizeOptions: [10, 20, 50, 100, 200, 500]
                }}
            />
        }*/
        {
            key: '6',
            disabled: !device?.props?.has_mappings,
            forceRender: device?.props?.has_mappings,
            label: <Tooltip
                overlay={device?.props?.has_mappings ? "" : "Für die Software dieses Gerätes wird der Import von DRW-Zuordnungen nicht unterstützt"}
            >
                <IconSet type='mapping'/> DRW
            </Tooltip>,
            children: <ApiTable
                searchable
                buttons={
                    <Space size={24}>
                        <p></p>
                        {
                            device?.props?.drwmapping_upload_date ?
                                <p>Stand: {device?.props?.drwmapping_upload_date.format("DD.MM.YYYY hh:mm")}</p>
                                : <></>
                        }
                        <CustomButton
                            mode={'delete'}
                            tooltip={'Alle Zuordnungen löschen'}
                            onClick={
                                () => Modal.confirm({
                                    title: "Zuordnungen löschen?",
                                    content: "Sollen wirklich alle Zuordnungen für dieses Gerät gelöscht werden? Diese Aktion kann nicht rückgängig gemacht werden!",
                                    maskClosable: true,
                                    cancelText: 'Abbrechen',
                                    okText: 'Löschen',
                                    okType: 'danger',
                                    autoFocusButton: 'cancel',
                                    onOk: () => {
                                        del("devices/" + device?.props?.uuid + "/mappings").then(() => {
                                        }).fail(
                                            () => {
                                                Modal.error({
                                                    title: "Fehler beim Löschen",
                                                    content: "Die Zuordnungen konnten nicht gelöscht werden!"
                                                })
                                            }
                                        )
                                    }
                                })
                            }
                        />
                    </Space>
                }
                title={<>Zugeordnete Dosisreferenzwerte</>}
                id={"Device_Mapping"}
                url={"devices/" + device?.props?.uuid + "/mappings"}
                reloadSignals={[useAppSelector(state => state.reloadSignals.mappings)]}
            />
        },
        {
            key: '7',
            forceRender: false,
            label: <><IconSet type='note'/> Todos</>,
            children:
                <ApiTable
                    title={<>Todos des Geräts</>}
                    id="device_todos"
                    url={"todos"}
                    url_params={"device=" + device?.props?.uuid}
                    storeFilters={true}
                    reloadSignals={[
                        useAppSelector(state => state.reloadSignals.todo),
                    ]}
                    actions={(text: any, record: any) =>
                            <div style={{display: "inline-flex"}}>
                                <CustomButton
                                    mode={'delete'}
                                    noBorder
                                    onClick={() => Modal.confirm({
                                        title: "Aufgabe überspringen",
                                        content: "Sollen diese Aufgabe wirklich übersprungen werden? Das bearbeiten der Aufgabe löst diesen Eintrag automatisch.",
                                        maskClosable: true,
                                        cancelText: 'Abbrechen',
                                        okText: 'Überspringen',
                                        okType: 'danger',
                                        autoFocusButton: 'cancel',
                                        onOk: () => {
                                            del("todos/" + record.key).catch((error) => {
                                                notification.error({
                                                    message: "Fehlende Berechtigungen",
                                                    description: "Todo konnte nicht als erledigt markiert werden.",
                                                    duration: 30,
                                                })
                                            })
                                        }
                                    })
                                    }
                                    tooltip={"Aufgabe überspringen"}
                                />

                                {record.is_editable ? (
                                    <CustomButton
                                        mode={'edit'}
                                        noBorder
                                        onClick={() => {
                                            get("todos/" + record.key).done((response) => {
                                                dispatch(openDrawer({key: 'todo', object: new Todo(response.data.todo)}))
                                            })

                                        }}
                                        tooltip={"Aufgabe bearbeiten"}
                                    />
                                ) : (
                                    <div style={{width: "2rem", height: "1rem"}} />
                                )
                                }

                            </div>
                    }
                    buttons={
                        <CustomButton
                            mode={'add'}
                            tooltip={"Neues Todo hinzufügen"}
                            onClick={() => dispatch(openDrawer({key: 'todo', object: new Todo({
                                    customer: customer.dump(),
                                    device: device.dump(),
                                })}))}
                        />
                    }
                />
        }
    ]

    const tabs = (
        <Tabs
            defaultActiveKey={device?.props?.comment ? "1" : "2"}
            style={{minHeight: '100%'}}
            tabBarStyle={{paddingLeft: '12px', marginBottom: 12}}
            //@ts-ignore
            items={tabItems}
        />
    )

    switch (state) {
        case ViewState.loading:
            return (
                <>
                    {breadcrumb}
                    <Loading/>
                </>
            )
        case ViewState.success:
            return (
                <>
                    {breadcrumb}
                    {disabledAlert}
                    {petAlert}
                    {pageHeader}
                    {tabs}
                </>
            )

        case ViewState.failed:
        default:
            return (
                <>
                    {breadcrumb}
                    <Result
                        status="404"
                        title="Nicht gefunden"
                        subTitle="Entschuldigung, der gesuchte Kunde konnte nicht gefunden werden!"
                    />
                </>
            )
    }
}